/* Freedom In Me Systems Alignment Model — Radial SVG */
import { useState } from "react";

const PILLARS = [
  {
    letter: "A",
    name: "Alignment",
    system: "Operations",
    angle: -90,
    color: "#1e5ab0",
    delivers: "Clarifies priorities, ownership, and cross-functional alignment.",
    impact: "Reduces wasted effort and operational friction.",
  },
  {
    letter: "L",
    name: "Leadership",
    system: "Leadership",
    angle: -18,
    color: "#16407e",
    delivers: "Leadership development, coaching, and modern workforce leadership.",
    impact: "Improves retention, engagement, and performance.",
  },
  {
    letter: "I",
    name: "Intelligence",
    system: "AI & Technology",
    angle: 54,
    color: "#103060",
    delivers: "AI adoption, workflow intelligence, and smarter systems.",
    impact: "Increases productivity, speed, and decision quality.",
  },
  {
    letter: "G",
    name: "Governance",
    system: "Execution",
    angle: 126,
    color: "#0a1f44",
    delivers: "Execution systems, accountability structures, and operational discipline.",
    impact: "Creates consistency, ownership, and measurable execution.",
  },
  {
    letter: "N",
    name: "Next",
    system: "Workforce",
    angle: 198,
    color: "#07142b",
    delivers:
      "Workforce attraction, talent readiness, modernization, and future-focused capability building.",
    impact:
      "Strengthens recruitment, adaptability, retention, and long-term performance.",
  },
];

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export default function AlignDiagram() {
  const [active, setActive] = useState(0);
  const cx = 300, cy = 300;
  const orbit = 200;
  const nodeR = 58;
  const activePillar = PILLARS[active];

  return (
    <div className="relative w-full" data-testid="align-radial-diagram">
      <svg
        viewBox="0 0 600 600"
        className="w-full h-auto"
        role="img"
        aria-label="Freedom In Me Systems Alignment Model"
      >
        <defs>
          <radialGradient id="bgGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#16407e" stopOpacity="0.08" />
            <stop offset="70%" stopColor="#0a1f44" stopOpacity="0.02" />
            <stop offset="100%" stopColor="#0a1f44" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="coreGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0a1f44" />
            <stop offset="100%" stopColor="#1e5ab0" />
          </linearGradient>
        </defs>

        {/* Backdrop glow */}
        <circle cx={cx} cy={cy} r="280" fill="url(#bgGrad)" />

        {/* Concentric rings */}
        {[80, 130, 180, 230, 270].map((r, i) => (
          <circle
            key={r}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#103060"
            strokeOpacity={0.08 + i * 0.025}
            strokeDasharray={i % 2 ? "2 6" : "0"}
          />
        ))}

        {/* Cross hairlines */}
        <line x1={cx - 270} y1={cy} x2={cx + 270} y2={cy} stroke="#103060" strokeOpacity="0.08" />
        <line x1={cx} y1={cy - 270} x2={cx} y2={cy + 270} stroke="#103060" strokeOpacity="0.08" />

        {/* Connector lines from core to pillars */}
        {PILLARS.map((p, i) => {
          const pt = polar(cx, cy, orbit, p.angle);
          const isActive = active === i;
          return (
            <line
              key={`line-${i}`}
              x1={cx}
              y1={cy}
              x2={pt.x}
              y2={pt.y}
              stroke={isActive ? p.color : "#16407e"}
              strokeOpacity={isActive ? 0.9 : 0.25}
              strokeWidth={isActive ? 2 : 1}
            />
          );
        })}

        {/* Outer pulse ring */}
        <circle
          cx={cx}
          cy={cy}
          r={orbit + nodeR + 12}
          fill="none"
          stroke="#1e5ab0"
          strokeOpacity="0.18"
          strokeDasharray="1 8"
          style={{ animation: "pulseRing 5s ease-in-out infinite" }}
        />

        {/* Pillars (orbiting nodes) */}
        {PILLARS.map((p, i) => {
          const pt = polar(cx, cy, orbit, p.angle);
          const isActive = active === i;
          return (
            <g
              key={p.letter}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
              tabIndex={0}
              style={{ cursor: "pointer", transition: "transform 0.4s ease" }}
              data-testid={`pillar-node-${p.letter}`}
            >
              <circle
                cx={pt.x}
                cy={pt.y}
                r={nodeR + (isActive ? 6 : 0)}
                fill={isActive ? p.color : "#ffffff"}
                stroke={p.color}
                strokeWidth={isActive ? 2 : 1.5}
                style={{ transition: "all 0.45s cubic-bezier(.2,.7,.2,1)" }}
              />
              <text
                x={pt.x}
                y={pt.y - 4}
                textAnchor="middle"
                fontFamily="Fraunces, serif"
                fontSize="34"
                fontWeight="600"
                fill={isActive ? "#fbf9f4" : p.color}
                style={{ transition: "fill 0.4s ease" }}
              >
                {p.letter}
              </text>
              <text
                x={pt.x}
                y={pt.y + 22}
                textAnchor="middle"
                fontFamily="Inter, sans-serif"
                fontSize="10"
                letterSpacing="2.6"
                fontWeight="600"
                fill={isActive ? "#e2c789" : "#5a6378"}
                style={{ transition: "fill 0.4s ease" }}
              >
                {p.name.toUpperCase()}
              </text>
            </g>
          );
        })}

        {/* Core */}
        <circle cx={cx} cy={cy} r="70" fill="url(#coreGrad)" />
        {/* Inner gold accent ring — tight inside the core, no bleed */}
        <circle cx={cx} cy={cy} r="62" fill="none" stroke="#c9a45a" strokeOpacity="0.55" strokeWidth="1" />
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontFamily="Fraunces, serif"
          fontSize="16"
          fill="#fbf9f4"
          fontWeight="500"
        >
          Freedom In Me
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="3"
          fill="#e2c789"
          fontWeight="700"
        >
          SYSTEMS ALIGNMENT
        </text>
      </svg>

      {/* Live info panel — reveals as user hovers/focuses pillars */}
      <div
        className="mt-8 bg-white border border-[rgba(10,31,68,0.1)] p-6 md:p-8 transition-all duration-500"
        data-testid="pillar-info-panel"
        key={activePillar.letter}
      >
        <div className="flex items-start gap-6">
          <div
            className="font-display text-[64px] leading-none flex-shrink-0"
            style={{ color: activePillar.color }}
          >
            {activePillar.letter}
          </div>
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[var(--gold)] font-semibold">
              Pillar · {activePillar.name}
            </div>
            <div className="mt-1 text-[12px] tracking-[0.18em] uppercase text-[var(--muted)] font-medium">
              The {activePillar.system} system
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-5">
              <div>
                <div className="eyebrow">What it delivers</div>
                <p className="mt-2 text-[14px] leading-relaxed text-[var(--navy-900)]">
                  {activePillar.delivers}
                </p>
              </div>
              <div className="md:border-l md:border-[rgba(10,31,68,0.1)] md:pl-5">
                <div className="eyebrow">Business impact</div>
                <p className="mt-2 text-[14px] leading-relaxed text-[var(--navy-900)]">
                  {activePillar.impact}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pillar nav strip */}
        <div className="mt-6 pt-6 border-t border-[rgba(10,31,68,0.08)] flex flex-wrap items-center gap-2">
          {PILLARS.map((p, i) => (
            <button
              key={p.letter}
              onClick={() => setActive(i)}
              data-testid={`pillar-pill-${p.letter}`}
              className={`px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                active === i
                  ? "bg-[var(--navy-900)] text-[var(--bone)]"
                  : "bg-transparent text-[var(--muted)] hover:text-[var(--navy-900)]"
              }`}
            >
              {p.letter} · {p.name}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-4 text-center text-[12px] text-[var(--muted)]">
        Hover, focus, or tap any pillar to explore.
      </p>
    </div>
  );
}
